import { Offcanvas, Form, Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import ReactQuill from "react-quill-new";
import { quillImageHandler } from "../../utility/quilImageHandler";
import CommonQuill from "../../components/editor/CommonQuill";
import HelperService from "../../utility/HelperService";

interface EventFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
}
interface Tag {
  _id: string;
  name: string;
}
type FormData = {
  title: string;
  description: string;
  tagId: string;
  category?: string;
  publishedAt: string;
  keywords?: string;
  meta_description?: string;
  slug?: string;
  createdBy?: string;
  authority_name?: string;
  location?: string;
  closingDate?: string;
  type?: string;
  lastAppliedDate?: string;
};

const AddIndustryReport = ({ show, handleClose, data }: EventFormData) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger
  } = useForm<FormData>();

  const [isLoading, setLoading] = useState(false);
  const [banner, setBanner] = useState<File | string | null>(null);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<
    { key: string; name: string; fileUrl?: string }[]
  >([]);
  const [value, setEditorValue] = useState("");
  const [tagsList, setTagsList] = useState<any[]>([]);
  const [categoryList, setCategoryList] = useState<any[]>([]);
  const quillRef = useRef<any>(null);
  const [customErrors, setCustomErrors] = useState<{
    description?: string;
  }>({});
  const titleValue = watch("title");
  useEffect(() => {

    //Edit mode me slug ko kabhi change nahi karna
    if (data?._id) return;

    if (!titleValue) {
      setValue("slug", "");
      return;
    }

    // ✅ Only auto-generate in Add mode
    if (!data?._id) {
      const slug = HelperService.generateSlug(titleValue);
      setValue("slug", slug);
    }
  }, [titleValue, data]);

  useEffect(() => {
    if (show) {
      const storedUser = localStorage.getItem("UserData");
      const userData = data?.user || (storedUser ? JSON.parse(storedUser) : null);
      const formattedDate = data?.publishedAt
        ? new Date(data.publishedAt).toISOString().split("T")[0]
        : "";
      const formattedClosingDate = data?.closingDate
        ? new Date(data.closingDate).toISOString().split("T")[0]
        : "";
      const formattedLastAppiledDate = data?.lastAppliedDate
        ? new Date(data.lastAppliedDate).toISOString().split("T")[0]
        : "";
      reset({
        title: data?.title || "",
        description: data?.description || "",
        type: data?.type || "",
        tagId: data?.tagId?._id || data?.tagId || "",
        publishedAt: formattedDate,
        keywords: data?.keywords || "",
        meta_description: data?.meta_description || "",
        slug: data?.slug || "",
        authority_name: data?.authority_name || "",
        location: data?.location || "",
        closingDate: formattedClosingDate,
        lastAppliedDate: formattedLastAppiledDate,
        category: data?.category?._id || data?.category || "",
        createdBy: data?.createdBy?.fullName ||  "",
      });
      setEditorValue(data?.description || "");
      fetchTags();
      fetchCategories();
      // ✅ preload existing banner & file
      if (data?.banner) {
        setBanner(data.banner);
      }
      // preload existing files (backend normalises legacy single file into files[])
      setExistingFiles(Array.isArray(data?.files) ? data.files : []);
      setNewFiles([]);
    } else {
      reset({});
      setBanner(null);
      setExistingFiles([]);
      setNewFiles([]);
    }
  }, [show, data, reset]);

  const isQuillEmpty = (value?: string) => {
    if (!value) return true;

    // remove html tags, &nbsp; and spaces
    const text = value
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };
  const validateInfoTab = async () => {
    const titleValid = await trigger(["title"]);

    const newErrors: any = {};

    if (isQuillEmpty(value)) {
      newErrors.description = "Description is required";
    }

    setCustomErrors(newErrors);

    // 🔥 IMPORTANT: return actual validity
    return titleValid && !newErrors.description;
  };
  const types = () => [
    { label: "Solar", value: "SOLAR" },
    { label: "Solar + BESS", value: "SOLAR_BESS" },
    { label: "BESS", value: "BESS" },
    { label: "EV", value: "EV" },
    { label: "Charging Station", value: "CHARGING_STATION" },
    { label: "FDRE", value: "FDRE" },
    { label: "RTC", value: "RTC" },
    { label: "RE + BESS", value: "RE_BESS" },
    { label: "PSP", value: "PSP" },
  ];
  const fetchTags = async () => {
    try {
      const res: any = await WebService.getAPI({
        action: `tags/get-tags/TENDERS`, // no pagination params
        body: null,
      });

      const tags: Tag[] = res?.data || [];

      setTagsList(tags);

      // ✅ If editing existing data, auto-select the tag
      // if (data?.tagId) {
      //   const tagExists = tags.some((tag: Tag) => tag._id === data.tagId);
      //   if (tagExists) setValue("tagId", data.tagId);
      // }
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res: any = await WebService.getAPI({
        action: `categories/get-list/TENDERS`,
        body: null,
      });

      setCategoryList(res?.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };
  const onSubmit = async (request: FormData) => {
    const valid = await validateInfoTab();
    if (!valid) {
      toast.error("Please fill required fields");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    const storedUser = localStorage.getItem("UserData");
    const userData = data?.user || (storedUser ? JSON.parse(storedUser) : null);
    formData.append("title", request.title);

    const editor = quillRef.current?.getEditor();
    const html = editor?.root.innerHTML || "";
    formData.append("description", html || "");
    formData.append("type", request.type || "");
    formData.append("tagId", request.tagId || "");
    formData.append("category", request.category || "");
    formData.append("keywords", request.keywords || "");
    formData.append("meta_description", request.meta_description || "");
    formData.append("authority_name", request.authority_name || "");
    formData.append("location", request.location || "");
    if (request.publishedAt)
      formData.append("publishedAt", request.publishedAt);
    if (request.closingDate)
      formData.append("closingDate", request.closingDate);
    if (request.lastAppliedDate)
      formData.append("lastAppliedDate", request.lastAppliedDate);
    // ✅ Include slug if available
    if (request.slug) {
      formData.append("slug", request.slug);
    }
    // if (banner && banner instanceof File) {
    //   formData.append("banner", banner); // only send new banner file
    // }
    if (newFiles.length + existingFiles.length === 0) {
      toast.error("At least one file is required");
      setLoading(false);
      return;
    }
    // append newly selected files (multiple)
    newFiles.forEach((f) => formData.append("files", f));
    // tell the backend which existing files to keep
    formData.append(
      "existingFiles",
      JSON.stringify(existingFiles.map((f) => ({ key: f.key, name: f.name }))),
    );
    if (userData?._id || userData.id) {
      formData.append("createdBy", userData._id || userData.id);
    }
    const apiCall = data?._id
      ? WebService.fileUploadPutAPI({
        action: `tender/edit-tender/${data._id}`,
        body: formData,
      })
      : WebService.fileUploadAPI({
        action: `tender/add-tender`,
        body: formData,
      });

    apiCall
      .then((res: any) => {
        toast.success(res.message);
        reset();
        handleClose();
        setBanner(null);
        setNewFiles([]);
        setExistingFiles([]);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error.response?.data?.message || "An error occurred");
      });
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        setBanner(null);
        setNewFiles([]);
        setExistingFiles([]);
        handleClose();
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{data ? "Edit Tender" : "Add Tender"}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Main Content
            </span>
            {/* Title */}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Title <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title"
                {...register("title", { required: "Title is required" })}
              />
              {errors.title && (
                <div className="error-message">{errors.title.message}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="slugField">
              <Form.Label className="label">Slug</Form.Label>{" "}
              <span className="text-danger">*</span>
              <Form.Control
                type="text"
                placeholder="Auto-generated slug"
                {...register("slug")}
                value={watch("slug") || ""}
              />
            </Form.Group>
            {/* Description */}
            <Form.Group className="mb-2" controlId="eventDescription">
              <Form.Label className="label">
                Description <span className="text-danger">*</span>
              </Form.Label>
              <CommonQuill
                ref={quillRef}
                value={value}
                onChange={setEditorValue}
                storage="RESOURCES"
                initialHtml={show && data?.description ? HelperService.normalizeResizeHtml(data.description) : undefined}
                onClearError={() =>
                  setCustomErrors((p) => ({
                    ...p,
                    description: undefined,
                  }))
                }
              />
              {customErrors.description && (
                <div className="error-message">{customErrors.description}</div>
              )}
            </Form.Group>


            {/* Removed banner upload as per requirement 6 Nov 2025*/}
            {/* Banner Upload */}
            {/* <Form.Group className="mb-2" controlId="bannerFile">
            <Form.Label className="label">Upload Banner</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              className="custom-file-upload"
              onChange={(e) => {
                const input = e.target as HTMLInputElement;
                const selectedFile = input.files && input.files[0];
                if (selectedFile) {
                  setBanner(selectedFile);
                }
              }}
            />
          </Form.Group>

          {banner && (
            <div className="mb-2 event-file">
              <img
                src={
                  banner instanceof File ? URL.createObjectURL(banner) : banner
                }
                alt="Banner Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: 200,
                  objectFit: "contain",
                }}
              />
            </div>
          )} */}

            {/* Tags Dropdown */}
            <Form.Group className="mb-2" controlId="eventTag">
              <Form.Label className="label">
                Tag <span className="text-danger"> *</span>
              </Form.Label>

              <Form.Select
                {...register("tagId", { required: "Please select a tag" })}
                size="sm"
                value={watch("tagId") || ""}
              >
                <option value="">Select Tag</option>
                {tagsList.map((tag: { _id: string; name: string }) => (
                  <option key={tag._id} value={tag._id}>
                    {tag.name}
                  </option>
                ))}
              </Form.Select>

              {errors.tagId && (
                <div className="error-message">{errors.tagId.message}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="eventCategory">
              <Form.Label className="label">
                Category <span className="text-danger">*</span>
              </Form.Label>

              <Form.Select
                {...register("category", { required: "Please select a category" })}
                size="sm"
                value={watch("category") || ""}
              >
                <option value="">Select Category</option>
                {categoryList.map((cat: any) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>

              {errors.category && (
                <div className="error-message">{errors.category.message}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="eventTag">
              <Form.Label className="label">
                Type <span className="text-danger">*</span>
              </Form.Label>

              <Form.Select
                {...register("type", { required: "Please select a type" })}
                size="sm"
              >
                <option value="">Select Type</option>
                {types().map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Select>

              {errors.type && (
                <div className="error-message">{errors.type.message}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Authority Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter authority name"
                {...register("authority_name", {
                  required: "Authority name are required",
                })}
              />
              {errors.authority_name && (
                <div className="error-message">
                  {errors.authority_name.message}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Location <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter location"
                {...register("location", { required: "Location are required" })}
              />
              {/* {errors.location && (
                <div className="error-message">{errors.location.message}</div>
              )} */}
              {errors.location && (
                <div className="error-message">
                  {errors.location.message}
                </div>
              )}
            </Form.Group>
            {/* <Form.Group className="mb-2" controlId="isFeatured">
            <Form.Check
              type="checkbox"
              label="Featured"
              {...register("featured")}
            />
          </Form.Group> */}
            <Form.Group className="mb-2" controlId="createdBy">
              <Form.Label className="label">Created By</Form.Label>
              <Form.Control
                type="text"
                placeholder="Created by user"
                {...register("createdBy")}
                value={watch("createdBy") || ""}
                disabled
              />
            </Form.Group>
            <Form.Group className="mb-2" controlId="eventDate">
              <Form.Label className="label">
                Published Date <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                max={new Date().toISOString().split("T")[0]}
                {...register("publishedAt", { required: "Date is required" })}
              />
              {errors.publishedAt && (
                <div className="error-message">
                  {errors.publishedAt.message}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="closingDate">
              <Form.Label className="label">
                Closing Date <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                {...register("closingDate", {
                  required: "Closing Date is required",
                })}
              />
              {errors.closingDate && (
                <div className="error-message">
                  {errors.closingDate.message}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="closingDate">
              <Form.Label className="label">
                Last Applied Date <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                {...register("lastAppliedDate", {
                  required: "Last Applied Date is required",
                })}
              />
              {errors.lastAppliedDate && (
                <div className="error-message">
                  {errors.lastAppliedDate.message}
                </div>
              )}
            </Form.Group>
          </Card>

          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Media
            </span>

            {/* PDF Upload (multiple) */}
            <Form.Group className="mb-2" controlId="pdfFile">
              <Form.Label className="label">
                Upload PDF File(s) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="file"
                accept=".pdf"
                multiple
                className="custom-file-upload"
                onChange={(e) => {
                  const input = e.target as HTMLInputElement;
                  const selected = input.files ? Array.from(input.files) : [];
                  if (selected.length) {
                    setNewFiles((prev) => [...prev, ...selected]);
                  }
                  input.value = ""; // allow re-selecting the same file
                }}
              />
              <span className="text-primary" style={{ fontSize: "12px" }}>
                Note: Only PDF files are allowed. You can add multiple files.
              </span>
            </Form.Group>

            {/* Selected / existing files list */}
            {(existingFiles.length > 0 || newFiles.length > 0) && (
              <div className="mb-2">
                {existingFiles.map((f, idx) => (
                  <div
                    key={`existing-${idx}`}
                    className="d-flex align-items-center justify-content-between border rounded px-2 py-1 mb-1"
                  >
                    <a
                      href={f.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {f.name}
                    </a>
                    <span
                      className="text-danger cursor-pointer"
                      title="Remove"
                      onClick={() =>
                        setExistingFiles((prev) =>
                          prev.filter((_, i) => i !== idx),
                        )
                      }
                    >
                      ✕
                    </span>
                  </div>
                ))}
                {newFiles.map((f, idx) => (
                  <div
                    key={`new-${idx}`}
                    className="d-flex align-items-center justify-content-between border rounded px-2 py-1 mb-1"
                  >
                    <span>{f.name}</span>
                    <span
                      className="text-danger cursor-pointer"
                      title="Remove"
                      onClick={() =>
                        setNewFiles((prev) => prev.filter((_, i) => i !== idx))
                      }
                    >
                      ✕
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              SEO
            </span>
            <Form.Label className="label">Meta Description <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Meta Description"
              {...register("meta_description", {
                required: "Meta description is required",
              })}
            />
            {errors.meta_description && (
              <div className="error-message">
                {errors.meta_description.message}
              </div>
            )}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Keywords <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter keywords"
                {...register("keywords", { required: "Keywords are required" })}
              />
              {errors.keywords && (
                <div className="error-message">{errors.keywords.message}</div>
              )}
            </Form.Group>
          </Card>
        </Form>
      </Offcanvas.Body>
      <div className="d-flex justify-content-end p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddIndustryReport;
